import React, { useState, useEffect } from "react";
import Modal1 from "../../DIsplay/Modal/Modal1";
import TransitVisaForm from "./TransitVisaForm";
import BulkTransitVisaFormList from "./BulkTransitVisaFormList";
import { enqueueSnackbar } from "notistack";
import {
  bulkCreateVisaRequirementsApi,
  createVisaRequirementsApi,
  updateVisaRequirementsApi,
} from "../../../controllers/visaController";

function CreateOrUpdateTransitVisaModal({ open, setOpen, data, callback }) {
  const isEditing = !!data;

  const emptyForm = {
    originCountry: "",
    transitCountry: "",
    destinationCountry: "",
    passportNationality: "",
    requiresTransitVisa: false,
    requiresDestinationVisa: false,
    transitVisaType: "VisaFree",
    destinationVisaType: "VisaFree",
    notes: "",
    open: true,
  };

  const [tab, setTab] = useState("single");

  const [form, setForm] = useState(emptyForm);
  const [bulkForms, setBulkForms] = useState([{ ...emptyForm }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  useEffect(() => {
    if (!data) {
      if (open) {
        setForm(emptyForm);
        setBulkForms([{ ...emptyForm }]);
        setTab("single");
      }
    }
  }, [open]);

  const validateForm = (entry) => {
    if (!entry.originCountry) return "Origin country is required";

    if (!entry.transitCountry) return "Transit country is required";

    if (!entry.passportNationality) return "Passport nationality is required";

    if (
      entry.requiresTransitVisa === "" ||
      entry.requiresTransitVisa === undefined ||
      entry.requiresTransitVisa === null
    )
      return "Select if transit visa is required";

    if (
      entry.requiresDestinationVisa === "" ||
      entry.requiresDestinationVisa === undefined ||
      entry.requiresDestinationVisa === null
    )
      return "Select if destination visa is required";

    return null; // valid
  };

  const save = () => {
    if (tab === "single" || isEditing) {
      const error = validateForm(form);
      if (error) {
        enqueueSnackbar(error, { variant: "error" });
        return;
      }

      if (isEditing) {
        handleUpdate(form);
      } else {
        handleCreate(form);
      }
      return;
    }

    // BULK MODE
    for (let i = 0; i < bulkForms.length; i++) {
      const entry = bulkForms[i];
      const { open, ...cleanEntry } = entry;

      const error = validateForm(cleanEntry);
      if (error) {
        enqueueSnackbar(`Entry #${i + 1}: ${error}`, { variant: "error" });
        return;
      }
    }

    // No validation errors
    const clean = bulkForms.map(({ open, ...rest }) => rest);
    handleBulkCreate({ items: clean });
    // callback(clean);
    // setOpen(false);
  };

  const handleCreate = async (payload) => {
    setLoading(true);
    const res = await createVisaRequirementsApi(payload).finally(() =>
      setLoading(false)
    );
    if (res?.return) {
      enqueueSnackbar(
        "Transit visa requirements have been created successfully.",
        { variant: "success" }
      );
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } else {
      enqueueSnackbar(
        res?.msg ||
          "Failed to create transit visa requirements. Please try again.",
        { variant: "error" }
      );
    }
  };

  const handleBulkCreate = async (payload) => {
    setLoading(true);
    const res = await bulkCreateVisaRequirementsApi(payload).finally(() =>
      setLoading(false)
    );
    if (res?.return) {
      enqueueSnackbar(
        "Transit visa requirements have been created successfully.",
        { variant: "success" }
      );
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } else {
      enqueueSnackbar(
        res?.msg ||
          "Failed to create transit visa requirements. Please try again.",
        { variant: "error" }
      );
    }
  };

  const handleUpdate = async (payload) => {
    const newPayload = {
      originCountry: payload?.originCountry,
      transitCountry: payload?.transitCountry,
      destinationCountry: payload?.destinationCountry,
      passportNationality: payload?.passportNationality,
      requiresTransitVisa: payload?.requiresTransitVisa,
      requiresDestinationVisa: payload?.requiresDestinationVisa,
      transitVisaType: payload?.transitVisaType,
      destinationVisaType: payload?.destinationVisaType,
      notes: payload?.notes,
    };
    setLoading(true);
    const res = await updateVisaRequirementsApi(data?._id, newPayload).finally(
      () => setLoading(false)
    );
    if (res?.return) {
      enqueueSnackbar(
        "Transit visa requirements have been updated successfully.",
        { variant: "success" }
      );
      callback?.(res?.data);
      setOpen(false);
    } else {
      enqueueSnackbar(
        res?.msg ||
          "Failed to update transit visa requirements. Please try again.",
        { variant: "error" }
      );
    }
  };

  return (
    <Modal1 open={open} setOpen={setOpen} modalBorderRadius="rounded-2xl">
      <div className="w-full p-6 bg-white flex flex-col gap-6">
        <h2 className="text-xl font-semibold">
          {isEditing ? "Update Transit Visa" : "Create Transit Visa"}
        </h2>

        {/* Tabs - only when creating */}
        {/* Toggle (only for create mode) */}
        {!isEditing && (
          <div className="relative bg-gray-100 rounded-xl p-1 w-fit">
            <div className="flex relative z-10">
              {["single", "bulk"].map((key) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    tab === key
                      ? "text-white"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                  style={{ minWidth: "90px" }}
                >
                  {key === "single" ? "Single" : "Bulk"}
                </button>
              ))}
            </div>

            {/* Sliding indicator */}
            <div
              className={`absolute top-1 left-1 h-[calc(100%-0.5rem)] w-[calc(50%-0.5rem)] rounded-lg bg-blue-600 shadow transition-all duration-300 ease-out ${
                tab === "single" ? "translate-x-0" : "translate-x-full"
              }`}
            ></div>
          </div>
        )}

        {(tab === "single" || isEditing) && (
          <TransitVisaForm form={form} setForm={setForm} />
        )}

        {tab === "bulk" && !isEditing && (
          <BulkTransitVisaFormList
            forms={bulkForms}
            setForms={setBulkForms}
            emptyForm={emptyForm}
          />
        )}

        {/* Footer */}
        <div className="w-full flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={() => setOpen(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={save}
            className="w-32 h-12 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {loading ? (
              <div className="buttonLoader"></div>
            ) : isEditing ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </div>
      </div>
    </Modal1>
  );
}

export default CreateOrUpdateTransitVisaModal;
